import React, { useEffect, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Users, GraduationCap, Award, DollarSign, FileText, BookOpen, Mail, ScrollText, BarChart3, ShieldCheck, Library, FilePlus, ClipboardCheck } from 'lucide-react';
import { Crest } from '@/components/Brand';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { ProgramListPage, ProgramBuilderPage } from '@/pages/admin/ProgramBuilder';
import { CourseListPage, CourseBuilderPage } from '@/pages/admin/CourseBuilder';
import GradingDashboard from '@/pages/admin/GradingDashboard';
import SecurityFeed from '@/pages/admin/SecurityFeed';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, Legend,
} from 'recharts';

const NAV = [
  { to: '', label: 'Overview', icon: BarChart3, end: true },
  { to: 'builder/programs', label: 'Program Builder', icon: FilePlus },
  { to: 'builder/courses', label: 'Course Builder', icon: Library },
  { to: 'grading', label: 'Grading Dashboard', icon: ClipboardCheck },
  { to: 'students', label: 'Students', icon: Users },
  { to: 'enrollments', label: 'Enrollments', icon: GraduationCap },
  { to: 'certificates', label: 'Certificates', icon: Award },
  { to: 'orders', label: 'Revenue', icon: DollarSign },
  { to: 'applications', label: 'Applications', icon: FileText },
  { to: 'messages', label: 'Messages', icon: Mail },
  { to: 'email-events', label: 'Email Log', icon: ScrollText },
  { to: 'security', label: 'Security & Audit', icon: ShieldCheck },
];

export default function AdminDashboard() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-trjlight flex" data-testid="admin-dashboard">
      <aside className="w-64 bg-navy text-white flex flex-col">
        <div className="p-5 border-b border-white/10 flex items-center gap-3">
          <Crest size="sm" />
          <div>
            <div className="font-display font-semibold text-white">Admin Console</div>
            <div className="text-[10px] uppercase tracking-widest text-gold">TRJ Academy</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map(n => (
            <NavLink key={n.to} to={n.to} end={n.end}
              data-testid={`admin-nav-${n.to || 'overview'}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm transition-colors ${isActive ? 'bg-gold/15 text-gold' : 'text-white/80 hover:bg-white/5 hover:text-white'}`}>
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-white/10 text-xs text-white/60">
          <div className="font-semibold text-white">{user?.name}</div>
          <div>{user?.email}</div>
          <div className="mt-1 text-gold uppercase tracking-widest text-[10px]">{(user?.roles || []).join(' · ')}</div>
        </div>
      </aside>
      <main className="flex-1 overflow-x-hidden">
        <Routes>
          <Route index element={<Overview />} />
          <Route path="builder/programs" element={<ProgramListPage />} />
          <Route path="builder/programs/:id" element={<ProgramBuilderPage />} />
          <Route path="builder/courses" element={<CourseListPage />} />
          <Route path="builder/courses/:id" element={<CourseBuilderPage />} />
          <Route path="grading" element={<GradingDashboard />} />
          <Route path="students" element={<Students />} />
          <Route path="enrollments" element={<Enrollments />} />
          <Route path="certificates" element={<Certificates />} />
          <Route path="orders" element={<Orders />} />
          <Route path="applications" element={<Applications />} />
          <Route path="messages" element={<Messages />} />
          <Route path="email-events" element={<EmailLog />} />
          <Route path="security" element={<SecurityFeed />} />
          <Route path="*" element={<Navigate to="" replace />} />
        </Routes>
      </main>
    </div>
  );
}

function Header({ title, sub }) {
  return (
    <div className="bg-white border-b border-slate-200 px-8 py-6">
      <div className="text-xs uppercase tracking-widest text-gold font-semibold">{sub}</div>
      <h1 className="font-display font-semibold text-2xl text-navy mt-1">{title}</h1>
    </div>
  );
}

function Overview() {
  const [stats, setStats] = useState(null);
  const [orders, setOrders] = useState([]);
  useEffect(() => {
    api.get('/admin/stats').then(({ data }) => setStats(data)).catch(() => {});
    api.get('/admin/orders').then(({ data }) => setOrders(data)).catch(() => {});
  }, []);

  const revByProgram = Object.values((orders || []).filter(o => o.status === 'COMPLETED').reduce((acc, o) => {
    const k = o.program_slug;
    acc[k] = acc[k] || { name: o.program_slug.replace(/-/g, ' '), value: 0 };
    acc[k].value += o.amount;
    return acc;
  }, {}));
  const COLORS = ['#0B1F3A', '#D4AF37', '#1E7D4D', '#0E3263', '#8C7220'];

  return (
    <>
      <Header title="Overview" sub="Admin Console" />
      <div className="p-8 space-y-8">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat label="Students" value={stats?.students ?? '—'} icon={Users} />
          <Stat label="Enrollments" value={stats?.enrollments ?? '—'} icon={GraduationCap} />
          <Stat label="Certificates" value={stats?.certificates ?? '—'} icon={Award} accent />
          <Stat label="Revenue" value={stats ? `$${stats.revenue_usd.toLocaleString()}` : '—'} icon={DollarSign} />
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="bg-white border border-slate-200 rounded-xl p-6 lg:col-span-2">
            <h3 className="font-display font-semibold text-navy">Recent orders</h3>
            <div className="mt-4 h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={orders.slice(0, 10).reverse().map(o => ({ name: new Date(o.created_at).toLocaleDateString(), amount: o.amount, status: o.status }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                  <XAxis dataKey="name" stroke="#64748B" fontSize={11} />
                  <YAxis stroke="#64748B" fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="amount" fill="#0B1F3A" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <h3 className="font-display font-semibold text-navy">Revenue by program</h3>
            <div className="mt-4 h-72">
              {revByProgram.length === 0 ? <div className="text-sm text-slate-500 text-center pt-16">No completed orders yet</div> :
                <ResponsiveContainer width="100%" height={288} minWidth={0}>
                  <PieChart>
                    <Pie data={revByProgram} dataKey="value" nameKey="name" outerRadius={80}>
                      {revByProgram.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

function Stat({ label, value, icon: Icon, accent }) {
  return (
    <div className={`rounded-xl p-5 border ${accent ? 'bg-navy text-white border-navy' : 'bg-white border-slate-200'}`}>
      <div className="flex items-center justify-between">
        <div className={`text-xs uppercase tracking-widest ${accent ? 'text-gold' : 'text-slate-500'}`}>{label}</div>
        <Icon className={`h-4 w-4 ${accent ? 'text-gold' : 'text-navy'}`} />
      </div>
      <div className={`text-3xl font-display font-semibold mt-3 ${accent ? '' : 'text-navy'}`}>{value}</div>
    </div>
  );
}

function DataTable({ title, sub, columns, rows, testid }) {
  return (
    <>
      <Header title={title} sub={sub} />
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid={testid}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>{columns.map(c => <th key={c.key} className="px-4 py-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={columns.length} className="px-4 py-12 text-center text-slate-500">No data yet.</td></tr>}
                {rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100 hover:bg-slate-50">
                    {columns.map(c => <td key={c.key} className="px-4 py-3 text-slate-700">{c.render ? c.render(r) : r[c.key]}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  );
}

function Students() {
  const [rows, setRows] = useState([]);
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantEmail, setGrantEmail] = useState('');
  const [grantSlug, setGrantSlug] = useState('');
  const [progs, setProgs] = useState([]);
  const [activityOpen, setActivityOpen] = useState(false);
  const [activityFor, setActivityFor] = useState(null); // {user_id, name, email}
  const [activityData, setActivityData] = useState(null);
  const [activityBusy, setActivityBusy] = useState(false);
  const load = () => api.get('/admin/students').then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); api.get('/programs').then(({ data }) => setProgs(data.filter(p => !p.is_coming_soon))); }, []);
  const triggerReset = async (email) => {
    try {
      await api.post('/auth/admin/trigger-reset', { email });
      toast.success(`Reset link sent to ${email}`);
    } catch (e) { toast.error('Could not send reset email'); }
  };
  const openGrant = (email) => { setGrantEmail(email); setGrantSlug(progs[0]?.slug || ''); setGrantOpen(true); };
  const grant = async () => {
    try {
      await api.post('/admin/enrollments/grant', { email: grantEmail, program_slug: grantSlug });
      toast.success(`Access granted to ${grantEmail}`); setGrantOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Grant failed'); }
  };
  const openActivity = async (student) => {
    setActivityFor(student);
    setActivityData(null);
    setActivityOpen(true);
    setActivityBusy(true);
    try {
      const { data } = await api.get(`/admin/security/students/${student.user_id}/timeline`, { params: { limit: 200 } });
      setActivityData(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load activity');
    } finally { setActivityBusy(false); }
  };
  return (
    <>
      <DataTable title="Students" sub="People" testid="admin-students-table"
        columns={[
          { key: 'name', label: 'Name' },
          { key: 'email', label: 'Email' },
          { key: 'roles', label: 'Roles', render: r => (r.roles || []).join(', ') },
          { key: 'created_at', label: 'Joined', render: r => new Date(r.created_at).toLocaleDateString() },
          { key: 'actions', label: 'Actions', render: r => (
            <div className="flex gap-2 flex-wrap">
              <Button size="sm" variant="outline" onClick={() => openActivity(r)} data-testid={`activity-${r.email}`}>Activity</Button>
              <Button size="sm" variant="outline" onClick={() => openGrant(r.email)} data-testid={`grant-${r.email}`}>Grant access</Button>
              <Button size="sm" variant="outline" onClick={() => triggerReset(r.email)} data-testid={`trigger-reset-${r.email}`}>Reset password</Button>
            </div>
          ) },
        ]} rows={rows} />
      <Dialog open={grantOpen} onOpenChange={setGrantOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Grant program access</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Student</Label><Input value={grantEmail} disabled /></div>
            <div><Label>Program</Label>
              <select value={grantSlug} onChange={(e) => setGrantSlug(e.target.value)}
                className="w-full h-10 px-3 rounded-md border border-slate-300 text-sm" data-testid="grant-program-select">
                {progs.map(p => <option key={p.slug} value={p.slug}>{p.title}</option>)}
              </select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGrantOpen(false)}>Cancel</Button>
            <Button className="btn-navy" onClick={grant} data-testid="confirm-grant">Grant access</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StudentActivityDialog
        open={activityOpen}
        onOpenChange={setActivityOpen}
        student={activityFor}
        data={activityData}
        busy={activityBusy}
      />
    </>
  );
}

function Enrollments() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/admin/enrollments').then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return <DataTable title="Enrollments" sub="LMS" testid="admin-enrollments-table"
    columns={[
      { key: 'student_name', label: 'Student' },
      { key: 'student_email', label: 'Email' },
      { key: 'course_title', label: 'Course' },
      { key: 'is_complete', label: 'Status', render: r => r.is_complete ? <span className="text-trjgreen font-semibold">Complete</span> : <span className="text-slate-500">In progress</span> },
      { key: 'enrolled_at', label: 'Enrolled', render: r => new Date(r.enrolled_at).toLocaleDateString() },
    ]} rows={rows} />;
}

function Certificates() {
  const [rows, setRows] = useState([]);
  const [audit, setAudit] = useState(null);
  const load = () => api.get('/admin/certificates').then(({ data }) => setRows(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const revoke = async (num) => {
    const reason = window.prompt('Reason for revocation?');
    if (reason === null) return;
    await api.post(`/admin/certificates/${num}/revoke`, { reason });
    toast.success('Revoked');
    load();
  };
  const reissue = async (num) => {
    const reason = window.prompt('Reason for reissue?');
    if (reason === null) return;
    const { data } = await api.post(`/admin/certificates/${num}/reissue`, { reason });
    toast.success(`Reissued as ${data.certificate_number}`);
    load();
  };
  const viewAudit = async (num) => {
    const { data } = await api.get(`/admin/certificates/${num}/audit`);
    setAudit({ num, rows: data });
  };

  return (
    <>
      <Header title="Certificates" sub="Registrar" />
      <div className="p-8">
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="admin-certificates-table">
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 text-left">
                <tr>{['#','Student','Course','Issued','Status','Actions'].map(h => <th key={h} className="px-4 py-3 text-xs uppercase tracking-widest text-slate-500 font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.certificate_number} className="border-t border-slate-100">
                    <td className="px-4 py-3 font-mono text-gold">{r.certificate_number}</td>
                    <td className="px-4 py-3 text-slate-700">{r.student_name}</td>
                    <td className="px-4 py-3 text-slate-700">{r.course_title}</td>
                    <td className="px-4 py-3 text-slate-700">{new Date(r.issued_at).toLocaleDateString()}</td>
                    <td className="px-4 py-3">
                      {r.revoked ? <span className="text-red-600 text-xs uppercase tracking-widest font-semibold">Revoked</span> :
                       r.status === 'superseded' ? <span className="text-slate-500 text-xs uppercase tracking-widest font-semibold">Superseded</span> :
                       <span className="text-trjgreen text-xs uppercase tracking-widest font-semibold">Active</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" onClick={() => viewAudit(r.certificate_number)} data-testid={`audit-${r.certificate_number}`}>Audit</Button>
                        {!r.revoked && r.status !== 'superseded' && (
                          <>
                            <Button size="sm" variant="outline" onClick={() => reissue(r.certificate_number)} data-testid={`reissue-${r.certificate_number}`}>Reissue</Button>
                            <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => revoke(r.certificate_number)} data-testid={`revoke-${r.certificate_number}`}>Revoke</Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {rows.length === 0 && <tr><td colSpan={6} className="text-center text-slate-500 py-12">No certificates yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <Dialog open={!!audit} onOpenChange={(o) => !o && setAudit(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Audit trail · <span className="font-mono text-gold">{audit?.num}</span></DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto thin-scroll">
            {audit?.rows.map((a, i) => (
              <div key={i} className="border border-slate-200 rounded-md p-3 text-sm">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-navy uppercase tracking-widest text-xs">{a.event}</div>
                  <div className="text-xs text-slate-500">{new Date(a.at).toLocaleString()}</div>
                </div>
                {a.actor && <div className="text-xs text-slate-600 mt-1">Actor: {a.actor}</div>}
                {a.reason && <div className="text-xs text-slate-600">Reason: {a.reason}</div>}
                {a.predecessor && <div className="text-xs text-slate-600 font-mono">Supersedes: {a.predecessor}</div>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Orders() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/admin/orders').then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return <DataTable title="Revenue & Orders" sub="Billing" testid="admin-orders-table"
    columns={[
      { key: 'paypal_order_id', label: 'PayPal ID', render: r => <span className="font-mono text-xs">{r.paypal_order_id?.slice(0, 14)}…</span> },
      { key: 'program_slug', label: 'Program' },
      { key: 'amount', label: 'Amount', render: r => `$${r.amount.toFixed(2)}` },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Created', render: r => new Date(r.created_at).toLocaleString() },
    ]} rows={rows} />;
}

function Applications() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/admin/applications').then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return <DataTable title="Applications" sub="Admissions" testid="admin-applications-table"
    columns={[
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'program_interest', label: 'Interest' },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Date', render: r => new Date(r.created_at).toLocaleDateString() },
    ]} rows={rows} />;
}

function Messages() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/admin/contact-messages').then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return <DataTable title="Contact Messages" sub="Support" testid="admin-messages-table"
    columns={[
      { key: 'name', label: 'From' },
      { key: 'email', label: 'Email' },
      { key: 'department', label: 'Dept' },
      { key: 'subject', label: 'Subject' },
      { key: 'created_at', label: 'Date', render: r => new Date(r.created_at).toLocaleString() },
    ]} rows={rows} />;
}

function EmailLog() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get('/admin/email-events').then(({ data }) => setRows(data)).catch(() => {}); }, []);
  return <DataTable title="Email Events" sub="Resend Log" testid="admin-email-events-table"
    columns={[
      { key: 'kind', label: 'Kind' },
      { key: 'to', label: 'Recipient' },
      { key: 'subject', label: 'Subject' },
      { key: 'status', label: 'Status', render: r => <span className={r.status === 'SENT' ? 'text-trjgreen' : r.status === 'FAILED' ? 'text-red-600' : 'text-slate-500'}>{r.status}</span> },
      { key: 'sent_at', label: 'Time', render: r => new Date(r.sent_at).toLocaleString() },
    ]} rows={rows} />;
}


function StudentActivityDialog({ open, onOpenChange, student, data, busy }) {
  const events = data?.events || [];
  const fmt = (iso) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString(undefined, {
        year: 'numeric', month: 'short', day: '2-digit',
        hour: '2-digit', minute: '2-digit', hour12: false,
      });
    } catch { return iso; }
  };
  const pill = (status) => {
    if (!status) return null;
    const v = String(status).toLowerCase();
    let cls = 'bg-slate-100 text-slate-700';
    if (['ok', 'active', 'sent', 'approved', 'completed', 'created'].includes(v)) cls = 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200';
    else if (v.includes('fail') || ['rate_limited', 'revoked', 'rejected', 'bad_password'].includes(v)) cls = 'bg-red-50 text-red-700 ring-1 ring-red-200';
    else if (['superseded', 'revision_required', 'unknown_email', 'pending'].includes(v)) cls = 'bg-amber-50 text-amber-800 ring-1 ring-amber-200';
    return <span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${cls}`}>{status}</span>;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="student-activity-dialog">
        <DialogHeader>
          <DialogTitle>
            Student Activity
            {student && (
              <span className="block text-sm font-normal text-slate-500 mt-1">
                <span className="text-navy font-semibold">{student.name}</span> · {student.email}
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto -mx-6 px-6">
          {busy && <div className="py-12 text-center text-slate-500 text-sm">Loading timeline…</div>}
          {!busy && events.length === 0 && (
            <div className="py-12 text-center text-slate-500 text-sm">No activity recorded yet.</div>
          )}
          {!busy && events.length > 0 && (
            <ol className="relative border-l-2 border-slate-200 ml-3 space-y-5 pl-6 py-3">
              {events.map((e, i) => (
                <li key={i} className="relative" data-testid="activity-event">
                  <span className="absolute -left-[31px] top-1.5 h-3 w-3 rounded-full bg-gold ring-4 ring-white" />
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-semibold text-navy">{e.label || e.event}</span>
                    {pill(e.status)}
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-slate-400 font-mono mt-1">
                    {fmt(e.at)}
                    {e.ip && <span> · IP {e.ip}</span>}
                  </div>
                  {(e.course_title || e.course_id || e.program_id || e.certificate_number || e.submission_id) && (
                    <div className="text-xs text-slate-600 mt-1 space-x-3">
                      {e.course_title && <span>🎓 {e.course_title}</span>}
                      {!e.course_title && e.course_id && <span className="font-mono">🎓 {e.course_id}</span>}
                      {e.program_id && <span className="font-mono">📚 {e.program_id}</span>}
                      {e.certificate_number && <span className="font-mono">📜 {e.certificate_number}</span>}
                      {e.submission_id && (
                        <span className="font-mono">📝 {e.submission_id}{e.kind ? ` (${e.kind})` : ''}</span>
                      )}
                    </div>
                  )}
                  {(e.actor || e.feedback || e.grade || e.reason) && (
                    <div className="text-xs text-slate-600 mt-1 space-x-3">
                      {e.actor && <span>by <span className="font-semibold">{e.actor}</span></span>}
                      {e.grade && <span>grade <span className="font-semibold">{e.grade}</span></span>}
                      {e.reason && <span className="italic">— {e.reason}</span>}
                      {e.feedback && <span className="italic">— &ldquo;{e.feedback}&rdquo;</span>}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          )}
        </div>

        <DialogFooter>
          <div className="text-xs text-slate-500 mr-auto">
            {data ? `${events.length} event(s) shown` : ''}
          </div>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-activity">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
